<template>
  <v-combobox
    v-model="emailList"
    ref="combobox"
    :items="[]"
    :label="label || 'Enter emails'"
    multiple
    chips
    small-chips
    clearable
    :rules="rulesArray"
    :error-messages="errorMessage"
    @input="handleInput"
    @blur="processEmails"
    hint="Type an email and press Enter, or use comma for multiple emails (e.g. email1, email2, email3)"
    persistent-hint
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-slot:selection="data">
      <v-chip
        :key="data.item + '-' + data.index"
        small
        close
        :color="isInvalidEmail(data.item) ? 'error' : undefined"
        @click:close="removeEmail(data.index)"
        v-bind="$attrs"
        v-on="$listeners"
      >
        {{ data.item }}
      </v-chip>
    </template>
  </v-combobox>
</template>

<script>
export default {
  name: "MultiEmailField",
  props: {
    value: {
      type: [Array, String],
      default: () => []
    },
    rules: {
      type: Array,
      default: () => []
    },
    notRequired: {
      type: Boolean,
      default: false
    },
    arrayFormat: {
      type: Boolean,
      default: false
    },
    label: String
  },
  data() {
    return {
      emailList: [],
      rulesArray: [],
      errorMessage: null,
      debounceTimer: null
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.emailList = [...val];
        } else if (typeof val === "string" && val.length > 0) {
          this.emailList = val
            .split(",")
            .map(e => e.trim())
            .filter(Boolean);
        } else {
          this.emailList = [];
        }
      }
    }
  },
  mounted() {
    this.resetRulesArray();
  },
  methods: {
    resetRulesArray() {
      this.rulesArray = [];
      if (this.notRequired) {
        this.rulesArray.push(
          v => (!!v && (Array.isArray(v) ? v.length > 0 : v.length > 0)) || "Email field is required."
        );
      }
      if (this.rules && this.rules.length) {
        this.rulesArray.push(...this.rules);
      }
      this.rulesArray.push(this.validateEmail);
    },

    validateEmail(value) {
      if (!value || (Array.isArray(value) && value.length === 0)) return true;

      const emails = Array.isArray(value) ? value : [value];
      const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      for (let email of emails) {
        if (!email || email.length === 0) return "Email cannot be empty";
        if (!regex.test(email)) return `Valid E-mail is required: ${email}`;
        if (email.length > 100) return `Email must be less than 100 characters: ${email}`;
      }

      // Duplicate detection (case-insensitive)
      const lowerCaseEmails = emails.map(e => e.toLowerCase());
      const duplicates = lowerCaseEmails.filter((e, i, arr) => arr.indexOf(e) !== i);
      if (duplicates.length) return `Duplicate email found: ${duplicates[0]}`;

      return true;
    },

    handleInput() {
      this.processEmails();
    },

    processEmails() {
      this.emailList = this.emailList
        .flatMap(e => e.split(","))
        .map(e => e.trim())
        .filter(e => e.length > 0);

      // Update error message live
      const result = this.validateEmail(this.emailList);
      this.errorMessage = result === true ? null : result;

      this.emitValue();
    },

    removeEmail(index) {
      this.emailList.splice(index, 1);
      this.processEmails();
    },

    emitValue() {
      if (this.arrayFormat) {
        this.$emit("input", [...this.emailList]);
      } else {
        this.$emit("input", this.emailList.join(","));
      }
    },

    isInvalidEmail(email) {
      // Validate a specific email in the list (format or duplicate)
      const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (!regex.test(email)) return true;

      const lowerCaseEmails = this.emailList.map(e => e.toLowerCase());
      const duplicates = lowerCaseEmails.filter((e, i, arr) => arr.indexOf(e) !== i);
      return duplicates.includes(email.toLowerCase());
    }
  }
};
</script>
